export interface MarkdownDoc {
  slug: string;
  title: string;
  date: string;
  summary: string;
  type: 'essay' | 'investigation' | 'project';
  tags: string[];
  author?: string | string[];
  password?: string;
  coverImage?: string;
  featured: boolean;
  published: boolean;
  draft: boolean;
  mock: boolean;
  hidden: boolean;
  readingTime: string;
  github?: string;
  link?: string;
  content: string;
  htmlContent: string;
}

export interface FilterOptions {
  includeDrafts?: boolean;
  includeMock?: boolean;
  includeHidden?: boolean;
}

export function parseFrontmatter(raw: string): { data: Record<string, any>; body: string } {
  const frontmatterRegex = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?([\s\S]*)$/;
  const match = raw.match(frontmatterRegex);

  if (!match) {
    return { data: {}, body: raw };
  }

  const yamlBlock = match[1];
  const body = match[2];
  const data: Record<string, any> = {};

  yamlBlock.split('\n').forEach(line => {
    const colonIdx = line.indexOf(':');
    if (colonIdx !== -1) {
      const key = line.slice(0, colonIdx).trim();
      let valueStr = line.slice(colonIdx + 1).trim();

      if ((valueStr.startsWith('"') && valueStr.endsWith('"')) || (valueStr.startsWith("'") && valueStr.endsWith("'"))) {
        valueStr = valueStr.slice(1, -1);
      }

      if (valueStr.startsWith('[') && valueStr.endsWith(']')) {
        const items = valueStr
          .slice(1, -1)
          .split(',')
          .map(s => s.trim().replace(/^['"]|['"]$/g, ''))
          .filter(Boolean);
        data[key] = items;
      } else if (valueStr === 'true') {
        data[key] = true;
      } else if (valueStr === 'false') {
        data[key] = false;
      } else {
        data[key] = valueStr;
      }
    }
  });

  return { data, body };
}

export function compileMarkdownToHtml(markdown: string): string {
  let html = markdown;

  // Code blocks ```ts ... ```
  html = html.replace(/```(\w*)\r?\n([\s\S]*?)```/g, (_, lang, code) => {
    const escapedCode = code.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    return `<pre><code class="language-${lang || 'text'}">${escapedCode.trim()}</code></pre>`;
  });

  // Blockquotes
  html = html.replace(/^>\s+(.*)$/gm, 'blockquote>$1</blockquote>');

  // Headings
  html = html.replace(/^### (.*$)/gm, '<h3>$1</h3>');
  html = html.replace(/^## (.*$)/gm, '<h2>$1</h2>');
  html = html.replace(/^# (.*$)/gm, '<h1>$1</h1>');

  // Bold & Italics
  html = html.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  html = html.replace(/\*(.*?)\*/g, '<em>$1</em>');

  // Images & Videos
  html = html.replace(/!\[(.*?)\]\((.*?)\)/g, '<img src="$2" alt="$1" class="md-image" style="max-width:100%; border-radius:6px; margin: 1rem 0;" />');
  html = html.replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank" rel="noopener">$1</a>');

  // Inline code `code`
  html = html.replace(/`([^`]+)`/g, '<code>$1</code>');

  // Unordered Lists
  html = html.replace(/^\-\s+(.*$)/gm, '<li>$1</li>');
  html = html.replace(/(<li>.*<\/li>\s*)+/g, '<ul>$&</ul>');

  // Markdown Tables
  html = html.replace(/(?:^\|.+$\r?\n?)+/gm, (tableBlock) => {
    const lines = tableBlock.trim().split('\n').map(l => l.trim()).filter(Boolean);
    if (lines.length < 2) return tableBlock;

    let tableHtml = '<div class="table-container"><table>';
    let isHeader = true;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      if (/^\|(?:\s*:?-+:?\s*\|)+$/.test(line)) {
        isHeader = false;
        continue;
      }

      const cells = line.slice(1, line.endsWith('|') ? -1 : undefined).split('|').map(c => c.trim());
      const tag = isHeader ? 'th' : 'td';
      const row = cells.map(c => `<${tag}>${c}</${tag}>`).join('');

      if (isHeader) {
        tableHtml += `<thead><tr>${row}</tr></thead><tbody>`;
        isHeader = false;
      } else {
        tableHtml += `<tr>${row}</tr>`;
      }
    }

    tableHtml += '</tbody></table></div>';
    return tableHtml;
  });

  // Paragraphs for remaining text blocks
  const paragraphs = html
    .split(/\n\n+/)
    .map(p => {
      const trimmed = p.trim();
      if (!trimmed) return '';
      if (
        trimmed.startsWith('<') // Allows native HTML and Lit Web Components (<app-button>, <details>, <video>, etc.)
      ) {
        return trimmed;
      }
      return `<p>${trimmed.replace(/\n/g, '<br/>')}</p>`;
    })
    .join('\n');

  return paragraphs;
}

export function calculateReadingTime(text: string): string {
  const wordsPerMinute = 200;
  const wordCount = text.split(/\s+/).length;
  const minutes = Math.ceil(wordCount / wordsPerMinute);
  return `${minutes} min read`;
}

// Dynamically import any article-specific Lit Web Components defined in TypeScript files under /projects/
import.meta.glob('/projects/**/*.ts', { eager: true });

const projectsRaw = import.meta.glob('/projects/**/*.md', { query: '?raw', eager: true });

function loadDocs(
  globObj: Record<string, any>,
  options: FilterOptions = {}
): MarkdownDoc[] {
  const docs: MarkdownDoc[] = [];

  for (const path in globObj) {
    const rawContent = (globObj[path] as any).default || (globObj[path] as string);
    if (!rawContent) continue;

    const parts = path.split('/').filter(Boolean);
    const fileName = parts[parts.length - 1] || '';
    const parentFolder = parts.length > 2 ? parts[parts.length - 2] : '';
    const fileBaseName = fileName.replace(/\.md$/, '');

    // Strict Folder-per-Article Rule:
    // When inside a subfolder under /projects/, ONLY the markdown file whose name matches the folder name is published.
    // All other markdown files in that folder (e.g., research.md, notes.md) are treated as internal research files.
    const isMainArticleFile = !parentFolder || parentFolder === 'projects' || fileBaseName === parentFolder;
    const slug = isMainArticleFile ? (parentFolder && parentFolder !== 'projects' ? parentFolder : fileBaseName) : fileBaseName;
    const { data, body } = parseFrontmatter(rawContent);

    const isResearchFile =
      !isMainArticleFile ||
      path.includes('/research/') ||
      path.includes('/_') ||
      fileName.endsWith('.research.md') ||
      fileName.endsWith('.notes.md') ||
      data.research === true;

    const isPublished = data.published !== false && !isResearchFile;
    const isDraft = data.draft === true || isResearchFile;
    const isMock = data.mock === true;
    const isHidden = data.hidden === true || isResearchFile;

    if (!options.includeHidden) {
      if (!options.includeDrafts && (!isPublished || isDraft)) {
        continue;
      }
      if (!options.includeMock && (isMock || isHidden)) {
        continue;
      }
    }

    const rawAuthor = data.author || data.authors || 'Geireann Lindfield Roberts';
    const coverImage = data.coverImage || data.cover_image || data.image || data.ogImage;

    docs.push({
      slug,
      title: data.title || slug,
      date: data.date || '2026-07-01',
      summary: data.summary || '',
      type: data.type || 'project',
      tags: Array.isArray(data.tags) ? data.tags : [],
      author: rawAuthor,
      password: data.password ? String(data.password).trim() : undefined,
      coverImage: coverImage ? String(coverImage).trim() : undefined,
      featured: data.featured ?? true,
      published: isPublished,
      draft: isDraft,
      mock: isMock,
      hidden: isHidden,
      readingTime: calculateReadingTime(body),
      github: data.github,
      link: data.link,
      content: body,
      htmlContent: compileMarkdownToHtml(body),
    });
  }

  return docs.sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function getAllPosts(options?: FilterOptions): MarkdownDoc[] {
  return loadDocs(projectsRaw, { includeDrafts: false, includeMock: false, includeHidden: false, ...options });
}

export function getAllProjects(options?: FilterOptions): MarkdownDoc[] {
  return loadDocs(projectsRaw, { includeDrafts: false, includeMock: false, includeHidden: false, ...options });
}

export function getDocBySlug(slug: string): MarkdownDoc | undefined {
  const all = loadDocs(projectsRaw, { includeDrafts: true, includeMock: true, includeHidden: true });
  return all.find(d => d.slug === slug);
}
